import {
  BIGCOMMERCE_CHANNEL_COUNTRY,
  fetchPlatformGraphqlApi,
} from '@utils/platform/common'
import pMemoize from 'p-memoize'
import logger from '@utils/logger'
import * as Sentry from '@sentry/nextjs'

export const productQuery = `
  query($slug: String!, $country: String!) {
    products(input: {groupedBySlug: $slug, country: $country}) {
      name,
      productSlug,
      categoryIds,
      variantSku,
      productId,
      variantId,
      cin7StyleCode,
      styleCode,
      price,
      salePrice,
      calculatedPrice,
      currencyCode,
      modifiersId,
      reorderModifierId,
      metadataModifierId,
      type,
      productWidthInMm
      productHeightInMm
      productLengthInMm
      productBleedWidthInMm
      productBleedHeightInMm
      productBleedLengthInMm
      optionsValues {
        id
        label
        option_display_name
        option_id
      }
      customUrl {
        url
      }
      imageUrl
    },
  }
`

export const variantPricesQuery = `
  query($slug: String!, $country: String!) {
    products(input: {groupedBySlug: $slug, country: $country }) {
      price,
      variantSku,
      salePrice,
      calculatedPrice,
      optionsValues {
        id
        label
        option_display_name
        option_id
      }
    },
  }
`

export const productCategoryQueryById = `
  query($categoryId: ID) {
    category(input: { filter: { id: $categoryId }}) {
      id
      name
      slug
      country
      products {
        name,
        productSlug,
        categoryIds,
        variantSku,
        productId,
        variantId,
        cin7StyleCode,
        styleCode,
        price,
        salePrice,
        calculatedPrice,
        currencyCode,
        modifiersId,
        reorderModifierId,
        metadataModifierId,
        type,
        productWidthInMm
        productHeightInMm
        productLengthInMm
        productBleedWidthInMm
        productBleedHeightInMm
        productBleedLengthInMm
        optionsValues {
          id
          label
          option_display_name
          option_id
        }
        customUrl {
          url
        }
        imageUrl
      }
    }
  }
`

function flattenOptions(products: any[]) {
  return products
    .map((product) => {
      const options = product.optionsValues
      const productOptions = options.reduce((acc, current) => {
        const name = current.option_display_name.trim().toLowerCase()
        const value = current.label.toString()
        acc[name] = value
        return acc
      }, {})

      return {
        ...product,
        ...productOptions,
      }
    })
    .filter((product) => product.price)
}

export async function getProductsWithPrice(
  productSlug: string,
  country: string = BIGCOMMERCE_CHANNEL_COUNTRY
) {
  const fnLogger = logger.child({
    name: 'getProductsWithPrice',
  })

  if (!productSlug) {
    Sentry.captureException(new Error('No product slug provided'))
    fnLogger.error('No product slug provided')
    return []
  }

  const response = await fetchPlatformGraphqlApi(productQuery, {
    variables: {
      slug: productSlug,
      country,
    },
  })

  logger.debug(
    { productSlug, country, status: response.res.status },
    'getProductsWithPrice response'
  )

  const { products = [] } = response?.data ?? {}

  return flattenOptions(products)
}

function getQuantityOption(products: any[]) {
  return products
    .map((product) => {
      const options = product.optionsValues
      const quantityOption = options.filter(
        (option) => option.option_display_name === 'Quantity'
      )?.[0]
      delete product.optionsValues

      if (quantityOption) {
        return {
          ...product,
          quantity: quantityOption?.label,
        }
      }
      return null
    })
    ?.filter(Boolean)
    ?.filter((product) => product.price)
}

export async function getVariantPrices(
  productSlug: string,
  country: string = BIGCOMMERCE_CHANNEL_COUNTRY
) {
  const response = await fetchPlatformGraphqlApi(variantPricesQuery, {
    variables: {
      slug: productSlug,
      country,
    },
  })

  logger.debug(
    { productSlug, country, status: response.res.status },
    'getVariantPrices response'
  )

  const { products = [] } = response?.data ?? {}

  // Convert options to flat property
  return getQuantityOption(products)
}

export const getProductsWithPriceWithCache = pMemoize(
  getProductsWithPrice, // First param as cache key is good enough
  { maxAge: 1000 * 60 * 60 * 24 } // Milliseconds and cache a day for gtm info which doesn't change that much
)

export async function getProductsWithPriceFromCategoryId(
  categoryId: number | string
) {
  const response = await fetchPlatformGraphqlApi(productCategoryQueryById, {
    variables: {
      categoryId: Number(categoryId),
    },
  })

  const { products = [] } = response?.data?.category

  // Convert options to flat property
  return flattenOptions(products)
}

const productSumaryQuery = `query CategoryProducts($categorySlug: String!, $channelId: String!) {
  categoryProducts(categorySlug: $categorySlug, channelId: $channelId) {
    products {
      productSlug
      productName
      primaryImage {
        fileName
        title
        url
      }
      productDescription
      deliveryTypes {
        label
        maxDays
        minDays
      }
      productTags {
        label
        labelImage {
          title
          url
          fileName
          description
        }
      }
      productType
      urlSlug
      commerceDetails {
        minOrderQuantity
        pricePerUnit
        salePricePerUnit
      }
      imageBackgroundColor
      minOrderQuantityFallback
      saleTag {
        type
        name
        labelImage {
          description
          fileName
          title
          url
        }
        label
      }
      statusTag {
        type
        name
        labelImage {
          description
          fileName
          title
          url
        }
        label
      }
    }
    categoryTitle,
    categoryDescription,
    categoryTree {
      name
      slug
      children {
        name
        slug
        children {
          name
          slug
        }
      }
    }
  }
}`

export const getCategoryProductsData = async (categorySlug: string) => {
  const response = await fetchPlatformGraphqlApi(productSumaryQuery, {
    variables: {
      categorySlug,
      channelId: '' + process.env.BIGCOMMERCE_CHANNEL_ID,
    },
  })

  return response?.data?.categoryProducts
}
